/**
 * Wahl des Transports für Bon und Kassenlade (Android-Hülle, Codex Runde 3 Fund 2): genau ein Weg
 * je Vorgang – nativ über `window.BilanxGeraet` (nur mit reiner Bonquelle, Server-Wunsch S-1) oder
 * wie bisher über Server und Hardware-Bridge. Ohne Bonquelle bleibt der Bondruck beim Server-Weg;
 * nur die Lade läuft dann nativ.
 */
import type { Speicher } from './belegnummern';
import {
  DruckErgebnisUnklar,
  druckeBeleg,
  type DruckApi,
  type GeraeteRueckmeldung,
} from './bondruck';
import {
  druckeBelegAmGeraet,
  druckweg,
  oeffneLadeAmGeraet,
  type BilanxGeraet,
  type BonQuelle,
} from './geraet';

/** Bondruck für Belegansicht/Belege: `auto` = einmaliger Druck nach dem Verkauf, sonst Nachdruck-Taste. */
export type BonDruck = (belegnummer: string, opt?: { auto: boolean }) => Promise<string>;

/** Rückmeldung → erfüllt bei „gedruckt“, sonst Ausnahme (unklar als [DruckErgebnisUnklar]). */
function alsVersprechen(r: GeraeteRueckmeldung): string {
  if (r.ok === false) {
    throw new Error(r.text);
  }
  if (r.ok === null) {
    throw new DruckErgebnisUnklar(r.text);
  }
  return r.text;
}

export function erstelleBonDruck(opt: {
  api: DruckApi;
  speicher: Speicher;
  kassenId: string;
  bridgeZugelassen: boolean;
  geraet: BilanxGeraet | null;
  bonQuelle: BonQuelle | null;
}): BonDruck | undefined {
  const { api, speicher, kassenId, bridgeZugelassen, geraet, bonQuelle } = opt;
  if (kassenId === '') {
    return undefined;
  }
  if (druckweg(geraet, bonQuelle) === 'nativ' && geraet !== null && bonQuelle !== null) {
    return (belegnummer, o = { auto: false }) =>
      druckeBelegAmGeraet(geraet, bonQuelle, speicher, kassenId, belegnummer, {
        art: o.auto ? 'auto' : 'nachdruck',
      }).then(alsVersprechen);
  }
  if (!bridgeZugelassen) {
    return undefined;
  }
  // Heutiger Weg; Nachdruck eines gedruckten Belegs = „KOPIE“.
  return (belegnummer) => druckeBeleg(api, speicher, kassenId, belegnummer).then(alsVersprechen);
}

/** Kassenlade über die native Bridge; undefined → Server-Weg (`oeffneLade`). */
export function erstelleLadeAmGeraet(
  geraet: BilanxGeraet | null,
): ((schluessel: string) => Promise<GeraeteRueckmeldung>) | undefined {
  return geraet === null ? undefined : (schluessel) => oeffneLadeAmGeraet(geraet, schluessel);
}
